// The keyboard shortcuts this build actually has (UX spec 10), for the shortcut help and the command
// palette. Only what a key handler in the app implements is listed: tests/web/shell/shortcuts.test.ts
// ties every entry to its handler and fails when a handler has no entry here.
//
// Keys are named in words ("Ctrl", "Shift", "Up"), never as key or arrow glyphs (Allen's rule).
// "Mod" is Ctrl, or Cmd on a Mac; "Alt" is Option on a Mac.

export type KeyName = string;
/** One way to press it: the keys held together. */
export type Combo = readonly KeyName[];

export interface Shortcut {
  id: string;
  /** Each combo is an alternative ("F6" or "Shift F6"). */
  keys: readonly Combo[];
  action: string;
  /** Where it works, when that is narrower than the group. */
  where?: string;
}

export interface ShortcutGroup {
  id: string;
  title: string;
  note?: string;
  items: readonly Shortcut[];
}

export const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    id: 'anywhere',
    title: 'Anywhere',
    items: [
      { id: 'palette', keys: [['Mod', 'K']], action: 'Open the command palette: pages, teammates, calls and actions' },
      { id: 'help', keys: [['?']], action: 'Show this list of shortcuts', where: 'When you aren’t typing. You can turn it off in Settings.' },
      { id: 'regions', keys: [['F6'], ['Shift', 'F6']], action: 'Move to the next or previous area: masthead, page, phone, status bar' },
      { id: 'dialer', keys: [['Alt', 'Shift', 'D']], action: 'Go to the dialer to find a teammate or type a number' },
    ],
  },
  {
    id: 'calls',
    title: 'Calls',
    note: 'These work even while you are typing.',
    items: [
      { id: 'answer', keys: [['Alt', 'Shift', 'A']], action: 'Answer the incoming call' },
      { id: 'end', keys: [['Alt', 'Shift', 'E']], action: 'Decline the incoming call, or end the active call' },
      { id: 'mute', keys: [['Alt', 'Shift', 'M']], action: 'Mute or unmute' },
      { id: 'hold', keys: [['Alt', 'Shift', 'H']], action: 'Hold or resume' },
      { id: 'note', keys: [['Alt', 'Shift', 'N']], action: 'Go to the note field', where: 'On the live call, the wrap-up card after a call, or an open call’s page' },
      { id: 'drop', keys: [['Alt', 'Shift', 'V']], action: 'Drop the default voicemail and free your line for the next call', where: 'On an answered call you placed to an outside number' },
    ],
  },
  {
    id: 'call-window',
    title: 'Call window',
    note: 'In the call window, even while you are typing.',
    items: [
      { id: 'ask-focus', keys: [['Mod', 'Shift', 'K']], action: 'Go to the question box under the transcript' },
    ],
  },
  {
    id: 'palette',
    title: 'Command palette',
    items: [
      { id: 'palette-move', keys: [['Up'], ['Down']], action: 'Move through the results' },
      { id: 'palette-run', keys: [['Enter']], action: 'Open or run the highlighted result' },
      { id: 'palette-close', keys: [['Esc']], action: 'Close the palette' },
    ],
  },
  {
    id: 'lists',
    title: 'Lists, tabs and menus',
    items: [
      { id: 'list-move', keys: [['Up'], ['Down'], ['Home'], ['End']], action: 'Move through a list', where: 'Conversations, routing lists, routing decisions and the call timeline (which also takes Left and Right)' },
      { id: 'tabs', keys: [['Left'], ['Right'], ['Home'], ['End']], action: 'Switch tabs', where: 'Admin, Analytics, Routing and Phone menus' },
      { id: 'dial-tabs', keys: [['Left'], ['Right']], action: 'Switch between Teammate and Outside number', where: 'In the dialer' },
      { id: 'close', keys: [['Esc']], action: 'Close the open menu or dialog', where: 'Setup menu, account menu and dialogs' },
    ],
  },
  {
    id: 'messages',
    title: 'Messages',
    items: [
      { id: 'send', keys: [['Mod', 'Enter']], action: 'Send the message', where: 'In the message box' },
    ],
  },
  {
    id: 'notes',
    title: 'Call notes',
    items: [
      { id: 'note-save', keys: [['Mod', 'Enter']], action: 'Save the note', where: 'In a call’s note field, or while editing your note' },
    ],
  },
  {
    id: 'player',
    title: 'Recording player',
    note: 'With the position slider focused.',
    items: [
      { id: 'seek-back', keys: [['Left'], ['Down']], action: 'Back 5 seconds' },
      { id: 'seek-forward', keys: [['Right'], ['Up']], action: 'Forward 5 seconds' },
      { id: 'seek-back-long', keys: [['Page Down']], action: 'Back 30 seconds' },
      { id: 'seek-forward-long', keys: [['Page Up']], action: 'Forward 30 seconds' },
      { id: 'seek-ends', keys: [['Home'], ['End']], action: 'Go to the start or the end' },
    ],
  },
];

export type Platform = 'mac' | 'other';

export function detectPlatform(nav: { platform?: string; userAgent?: string; userAgentData?: { platform?: string } } | undefined = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { userAgentData?: { platform?: string } })): Platform {
  const name = nav?.userAgentData?.platform || nav?.platform || nav?.userAgent || '';
  return /mac|iphone|ipad|ipod/i.test(name) ? 'mac' : 'other';
}

/** A key's name as this platform's keyboard prints it. */
export function keyLabel(key: KeyName, platform: Platform): string {
  if (key === 'Mod') return platform === 'mac' ? 'Cmd' : 'Ctrl';
  if (key === 'Alt') return platform === 'mac' ? 'Option' : 'Alt';
  return key;
}

/** The whole shortcut in words, for hints and accessible names: "Ctrl+K", "F6 or Shift+F6". */
export function shortcutText(keys: readonly Combo[], platform: Platform): string {
  return keys.map(combo => combo.map(k => keyLabel(k, platform)).join('+')).join(' or ');
}

export function findShortcut(id: string): Shortcut | null {
  for (const group of SHORTCUT_GROUPS) for (const item of group.items) if (item.id === id) return item;
  return null;
}

/** Key events from fields and editable content: single-key shortcuts stay out of the way there. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as { tagName?: unknown; isContentEditable?: boolean; type?: string } | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  return !['button', 'checkbox', 'radio', 'range', 'reset', 'submit', 'color', 'file', 'image'].includes((el.type ?? 'text').toLowerCase());
}

export interface KeyInput {
  key: string;
  code: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  isComposing?: boolean;
  target: EventTarget | null;
}

/**
 * The shell's own shortcuts: Ctrl/Cmd+K (by `code`, like the call shortcuts, so the layout's
 * Option characters don't matter) and "?" (by the character typed, so it follows the layout).
 */
export function shellShortcut(event: KeyInput, singleKeyOn: boolean): 'palette' | 'help' | null {
  if (event.isComposing) return null;
  if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.code === 'KeyK') return 'palette';
  if (singleKeyOn && event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target)) return 'help';
  return null;
}

// WCAG 2.1.4: single-character shortcuts ("?") can be turned off. On by default.
const SINGLE_KEY = 'sidetone.shortcuts.single-key';
const singleKeyListeners = new Set<() => void>();
let singleKeyMemory: boolean | null = null;

export function singleKeyShortcutsOn(): boolean {
  try {
    return window.localStorage.getItem(SINGLE_KEY) !== 'off';
  } catch {
    return singleKeyMemory ?? true;
  }
}

export function setSingleKeyShortcuts(on: boolean): void {
  singleKeyMemory = on;
  try {
    if (on) window.localStorage.removeItem(SINGLE_KEY);
    else window.localStorage.setItem(SINGLE_KEY, 'off');
  } catch {
    // Storage unavailable: the choice applies until the page reloads.
  }
  for (const listener of singleKeyListeners) listener();
}

export function subscribeSingleKey(listener: () => void): () => void {
  singleKeyListeners.add(listener);
  return () => { singleKeyListeners.delete(listener); };
}
